import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { FindingDocType } from '@ast-data/shared';
import { useProjectDocs } from '../../db/useScopedDocs';
import { linkRowBuilder } from '../users/useUserLinks';
import LinkedRecordsDialog from '../users/LinkedRecordsDialog';
import type { CrewRecord } from './crewRecords';

/** Who the dialog is about: a name and the records naming them. */
export interface CrewRecordsTarget {
    name: string;
    records: CrewRecord[];
}

interface CrewRecordsDialogProps {
    projectId: string;
    /** Null keeps the dialog closed. */
    person: CrewRecordsTarget | null;
    onClose: () => void;
}

/**
 * One crew member's records in a project, each linked — opened from the
 * Grabungsmannschaft and from a Tagebuch's people of the day. Only for
 * `access CrewRecords`; the callers hide the button that opens it.
 *
 * Mounted beside every Tagebuch form and Grabungsmannschaft, so it reads the
 * project's Befunde only while it is open.
 */
export default function CrewRecordsDialog(props: CrewRecordsDialogProps) {
    return props.person ? (
        <OpenCrewRecordsDialog {...props} person={props.person} />
    ) : null;
}

function OpenCrewRecordsDialog({
    projectId,
    person,
    onClose
}: CrewRecordsDialogProps & { person: CrewRecordsTarget }) {
    const intl = useIntl();
    // The Befundnummern name the Plana and Profile among the records.
    const { results: findings } = useProjectDocs<FindingDocType>(
        'findings',
        projectId
    );
    const linkRow = useMemo(
        () =>
            linkRowBuilder(
                new Map(
                    findings.map((finding) => [
                        finding.id,
                        finding.findingNumber
                    ])
                ),
                new Map()
            ),
        [findings]
    );
    const rows = useMemo(
        () =>
            person.records.map((record) =>
                linkRow(record.entityType, record.doc, record.kindId)
            ),
        [person, linkRow]
    );

    return (
        <LinkedRecordsDialog
            open
            title={intl.formatMessage(
                { id: 'crew.records.title' },
                { name: person.name }
            )}
            rows={rows}
            showProject={false}
            onClose={onClose}
        />
    );
}
